/**
 * BaseSDKWrapper - Reusable base class for all SDK wrappers
 *
 * This base class provides:
 * 1. Standard error handling and logging
 * 2. Common data transformation methods
 * 3. Consistent interface for all SDK wrappers
 *
 * All service-specific SDK wrappers should extend this class.
 *
 * @example
 * export class DocumentSDKWrapper extends BaseSDKWrapper {
 *   constructor() {
 *     super('DocumentSDK');
 *     this.sdkMethods = getDocument();
 *   }
 *
 *   async getById(documentId: string): Promise<any> {
 *     return this.execute(
 *       () => this.sdkMethods.getDocumentDocumentByIdDocIdGet(documentId, {} as any),
 *       'getById'
 *     );
 *   }
 * }
 */

import { AxiosRequestConfig } from 'axios';

/**
 * Base class for all SDK wrappers
 *
 * Provides standard error handling, logging, and data transformation
 * that all SDK wrappers can inherit.
 */
export abstract class BaseSDKWrapper {
  /**
   * SDK methods object from generated SDK factory function
   * Must be initialized in the constructor of each extending class
   *
   * @example
   * constructor() {
   *   super('MyServiceSDK');
   *   this.sdkMethods = getMyService();
   * }
   */
  protected sdkMethods: any;

  /**
   * Name of the SDK wrapper for logging purposes
   */
  protected serviceName: string;

  /**
   * @param serviceName - Name of the service for logging (e.g., 'DocumentSDK')
   */
  constructor(serviceName: string) {
    this.serviceName = serviceName;
  }

  /**
   * Execute an SDK operation with standard error handling and logging
   *
   * This method wraps SDK calls to provide:
   * - Consistent error logging with service context
   * - Error re-throwing for caller handling
   * - Optional operation name for debugging
   *
   * @param operation - The SDK operation to execute
   * @param operationName - Name of the operation for logging (e.g., 'getById')
   * @returns Promise resolving to operation result
   *
   * @example
   * async getById(id: string): Promise<Document> {
   *   return this.execute(
   *     () => this.sdkMethods.getDocumentById(id),
   *     'getById'
   *   );
   * }
   */
  protected async execute<T>(
    operation: () => Promise<T>,
    operationName: string,
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      console.error(`[${this.serviceName}.${operationName}] Error:`, error);
      throw error;
    }
  }

  /**
   * Transform data for update operations
   *
   * Handles common data transformations needed for backward compatibility:
   * - Convert string amounts to numbers if needed
   * - Normalize date formats
   * - Flatten nested structures
   * - Rename fields if API changed
   *
   * Override this method in child classes for service-specific transformations.
   *
   * @param data - Partial data from caller (may be legacy format)
   * @returns Transformed data in SDK-compatible format
   *
   * @example
   * protected transformForUpdate(data: any): any {
   *   const transformed = super.transformForUpdate(data);
   *   // Add service-specific transformations
   *   if (transformed.amount && typeof transformed.amount === 'string') {
   *     transformed.amount = parseFloat(transformed.amount);
   *   }
   *   return transformed;
   * }
   */
  protected transformForUpdate(data: any): any {
    // Base implementation - pass through unchanged
    // Child classes can override for specific transformations
    return data;
  }

  /**
   * Transform data for create operations
   *
   * Similar to transformForUpdate, but for creation.
   * Add any defaults or required fields here.
   *
   * Override this method in child classes for service-specific transformations.
   *
   * @param data - Data from caller (may be legacy format)
   * @returns Transformed data in SDK-compatible format
   *
   * @example
   * protected transformForCreate(data: any): any {
   *   const transformed = super.transformForCreate(data);
   *   // Add service-specific defaults
   *   transformed.created_at = transformed.created_at || new Date().toISOString();
   *   return transformed;
   * }
   */
  protected transformForCreate(data: any): any {
    // Base implementation - pass through unchanged
    // Child classes can override for specific transformations
    return data;
  }

  /**
   * Transform response data from SDK to legacy format
   *
   * Use this when SDK response format differs from what components expect.
   * This ensures backward compatibility during migration.
   *
   * Override this method in child classes if response transformation is needed.
   *
   * @param response - Response from SDK
   * @returns Transformed response in legacy format
   *
   * @example
   * protected transformResponse(response: any): any {
   *   const transformed = super.transformResponse(response);
   *   // Add service-specific response transformations
   *   if (transformed.data) {
   *     transformed.results = transformed.data; // Legacy field name
   *     delete transformed.data;
   *   }
   *   return transformed;
   * }
   */
  protected transformResponse(response: any): any {
    // Base implementation - pass through unchanged
    // Child classes can override for specific transformations
    return response;
  }

  /**
   * Prepare FormData from object
   *
   * Many legacy endpoints use FormData encoding.
   * This helper converts JavaScript objects to FormData.
   *
   * Note: Generated SDK often handles this automatically via URLSearchParams.
   * Use this only if you need manual FormData construction.
   *
   * @param data - Object to convert to FormData
   * @returns FormData instance
   *
   * @example
   * const formData = this.prepareFormData({
   *   name: 'John Doe',
   *   email: 'john@example.com',
   * });
   */
  protected prepareFormData(data: Record<string, any>): FormData {
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
      const value = data[key];

      if (value !== undefined && value !== null) {
        if (value instanceof File || value instanceof Blob) {
          formData.append(key, value);
        } else if (Array.isArray(value)) {
          value.forEach((item) => {
            formData.append(key, String(item));
          });
        } else {
          formData.append(key, String(value));
        }
      }
    });

    return formData;
  }

  /**
   * Log operation for debugging
   *
   * Use this for detailed logging during development.
   * Can be disabled in production via environment variables.
   *
   * @param operationName - Name of the operation
   * @param params - Parameters passed to operation
   *
   * @example
   * async getById(id: string): Promise<Document> {
   *   this.logOperation('getById', { id });
   *   return this.execute(...);
   * }
   */
  protected logOperation(operationName: string, params?: any): void {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[${this.serviceName}.${operationName}]`, params);
    }
  }
}

/**
 * Type helper for SDK wrapper methods
 *
 * Use this to type-check SDK wrapper method signatures during migration.
 *
 * @example
 * export class DocumentSDKWrapper extends BaseSDKWrapper {
 *   // Implement methods from SDK
 * }
 */
export type SDKWrapperMethods<T> = {
  [K in keyof T]: T[K];
};
